import { useState } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useAuth } from '@/features/auth/providers/AuthProvider';
import useNotificationPermission from '@/features/notifications/hooks/useNotificationPermission';
import { useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';

const DISMISSED_KEY = 'robinhoot_notification_banner_dismissed';
const DISMISS_DURATION_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isDismissedRecently(): boolean {
  const raw = localStorage.getItem(DISMISSED_KEY);
  if (!raw) return false;
  const dismissedAt = Number(raw);
  if (Number.isNaN(dismissedAt)) return false;
  return Date.now() - dismissedAt < DISMISS_DURATION_MS;
}

/**
 * Lightweight banner that prompts users to enable push notifications.
 * Only shown when:
 * - The user is authenticated
 * - Browser supports notifications
 * - Permission has never been asked ('default')
 * - The banner hasn't been dismissed in the last 14 days
 *
 * Clicking "Settings" navigates to the profile notifications tab.
 */
export function NotificationBanner() {
  const { user } = useAuth();
  const { canAsk, permission } = useNotificationPermission({
    userId: user?.uid ?? null,
  });
  const navigate = useNavigate();

  const [dismissed, setDismissed] = useState(() => isDismissedRecently());

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  };

  const handleGoToSettings = () => {
    dismiss();
    navigate({ to: '/profile', search: { settings: 'notifications' } });
  };

  const handleDismiss = () => {
    dismiss();
  };

  // Don't show if: not logged in, already decided, dismissed, or unsupported
  const shouldShow = user && canAsk && !dismissed && permission !== 'unsupported';

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="bg-primary/5 border-b border-primary/15">
            <div className="max-w-7xl mx-auto px-4 py-3">
              <div className="flex items-center justify-center gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0 p-1.5 rounded-lg bg-primary/10">
                    <Bell className="size-4 text-primary" />
                  </div>
                  <p className="text-sm text-foreground truncate">
                    Enable notifications to stay updated
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" onClick={handleGoToSettings} className="text-xs h-7 px-3">
                    Settings
                  </Button>
                  <button
                    onClick={handleDismiss}
                    className="p-1 rounded-md hover:bg-primary/10 text-muted-foreground transition-colors"
                    aria-label="Dismiss notification banner"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
